"use client";

import React, { useEffect, useState, useMemo } from "react";
import { Card } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { toast } from "components/ui/toast";
import { Line, Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
} from "chart.js";
import { getCampaignsByIdRes, getCampaignsStatsRes } from "core";
import "chartjs-adapter-date-fns";
import ABCampaignResults from 'components/campaigns/ABCampaignResults';
import { isABCampaign } from 'core/features/dashboard/abTesting';

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale
);

interface CampaignGraphProps {
  campaignStatsDetails: any;
}

export default function CampaignGraph({
  campaignStatsDetails,
}: CampaignGraphProps) {
  const [graphData, setGraphData] = useState<any>(campaignStatsDetails ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [uniqueOpens, setUniqueOpens] = useState<any[]>([]);
  const [pieData, setPieData] = useState<any[]>([]);
  const [summarySeries, setSummarySeries] = useState<any[]>([]);

  useEffect(() => {
    let isMounted = true;

    if (!campaignStatsDetails?.id) {
      setGraphData(campaignStatsDetails ?? null);
      return () => {
        isMounted = false;
      };
    }

    async function loadGraphData() {
      try {
        setIsLoading(true);
        let mergedData = campaignStatsDetails ?? null;

        try {
          const campaignRes: any = await getCampaignsByIdRes(campaignStatsDetails.id);
          const campaignDetailData = campaignRes?.data ?? null;
          if (campaignDetailData) {
            mergedData = {
              ...mergedData,
              ...campaignDetailData,
            };
          }
        } catch (error) {
          console.error("Unable to load campaign detail payload for graph", error);
        }

        const res = await getCampaignsStatsRes(campaignStatsDetails.id, 0);
        if (!isMounted) return;
        setGraphData({
          ...mergedData,
          ...res,
        });
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setGraphData(campaignStatsDetails ?? null);
          toast.error("Unable to load graph data");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadGraphData();

    return () => {
      isMounted = false;
    };
  }, [campaignStatsDetails]);

  // === Parse time-wise email opens ===
  useEffect(() => {
    if (!graphData) {
      setUniqueOpens([]);
      setPieData([]);
      setSummarySeries([]);
      return;
    }

    const parsed = (graphData.time_wise_email_opens || []).map(
      (e: any) => {
        const dateParts = e[0].split(/[- :]/);
        const [year, month, day, hour, minute, second] = dateParts.map(Number);
        const utcDate = new Date(
          Date.UTC(year, month - 1, day, hour, minute, second)
        );
        return { x: utcDate, y: e[1] };
      }
    );
    setUniqueOpens(parsed);

    const pie = [
      {
        name: "Unique Opens",
        value: graphData.pie_chart_data?.unique_opens || Math.round(((graphData.total_opened_rate || 0) / 100) * (graphData.total_customers_mapped || 0)),
      },
      {
        name: "Bounced",
        value: graphData.pie_chart_data?.bounced || Math.round(((graphData.total_bounce_rate || 0) / 100) * (graphData.total_customers_mapped || 0)),
      },
      {
        name: "Not Opened",
        value: graphData.pie_chart_data?.not_opened || Math.max(
          0,
          (graphData.total_customers_mapped || 0) -
          Math.round(((graphData.total_opened_rate || 0) / 100) * (graphData.total_customers_mapped || 0)) -
          Math.round(((graphData.total_bounce_rate || 0) / 100) * (graphData.total_customers_mapped || 0))
        ),
      },
    ];
    setPieData(pie);

    setSummarySeries([
      { label: "Sent", value: graphData.total_sent_rate || graphData.get_campaign_stats_rates?.sent_rate || 0 },
      { label: "Delivered", value: graphData.total_delivery_rate || graphData.get_campaign_stats_rates?.delivery_rate || 0 },
      { label: "Opened", value: graphData.total_opened_rate || graphData.get_campaign_stats_rates?.opened_rate || 0 },
      { label: "Bounced", value: graphData.total_bounce_rate || graphData.get_campaign_stats_rates?.bounce_rate || 0 },
      { label: "Rejected", value: graphData.total_reject_rate || graphData.get_campaign_stats_rates?.reject_rate || 0 },
      { label: "Spam", value: graphData.total_spam_rate || graphData.get_campaign_stats_rates?.spam_rate || 0 },
    ]);
  }, [graphData]);

  // === Theme-aware color tokens (must be before any useMemo that references them) ===
  const isDarkMode = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const textColor  = isDarkMode ? "#cbd5e1" : "#475569";
  const titleColor = isDarkMode ? "#f1f5f9" : "#0f172a";
  const gridColor  = isDarkMode ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
  const tooltipBg  = isDarkMode ? "#0f172a" : "#1e293b";

  // === Line Chart Config ===
  const lineConfig = useMemo(() => {
    return {
      labels: uniqueOpens.map((p) => p.x),
      datasets: [
        {
          label: "Unique Opens (by time)",
          data: uniqueOpens,
          borderColor: "#6366f1",
          backgroundColor: "rgba(99,102,241,0.12)",
          pointBackgroundColor: "#6366f1",
          pointBorderColor: isDarkMode ? "#1e293b" : "#ffffff",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 7,
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [uniqueOpens, isDarkMode]);

  const lineOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Email Opens — Hourly",
        color: titleColor,
        font: { size: 14, weight: "bold" as const },
      },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: "#f1f5f9",
        bodyColor: "#cbd5e1",
        borderColor: isDarkMode ? "#334155" : "#e2e8f0",
        borderWidth: 1,
        cornerRadius: 10,
        padding: 10,
      },
    },
    scales: {
      x: {
        type: "time" as const,
        time: { unit: "hour", tooltipFormat: "MMM d, h:mm a" },
        title: { display: true, text: "Time", color: textColor },
        ticks: { color: textColor, font: { size: 11 } },
        grid: { color: gridColor },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Opens", color: textColor },
        ticks: { stepSize: 1, color: textColor, font: { size: 11 }, precision: 0 },
        grid: { color: gridColor },
        border: { display: false },
      },
    },
  };

  const summaryBarConfig = useMemo(() => {
    return {
      labels: summarySeries.map((item) => item.label),
      datasets: [
        {
          label: "Rate %",
          data: summarySeries.map((item) => item.value),
          backgroundColor: ["#6366f1", "#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6"],
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };
  }, [summarySeries]);

  const summaryBarOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Campaign Summary Rates",
        color: titleColor,
        font: { size: 14, weight: "bold" as const },
      },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: "#f1f5f9",
        bodyColor: "#cbd5e1",
        borderColor: isDarkMode ? "#334155" : "#e2e8f0",
        borderWidth: 1,
        cornerRadius: 10,
        padding: 10,
        callbacks: {
          label: (ctx: any) => ` ${Number(ctx.parsed.y || 0).toFixed(0)}%`,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: textColor, font: { size: 11 } },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { color: textColor, font: { size: 11 }, callback: (value: any) => `${value}%` },
        grid: { color: gridColor },
        border: { display: false },
      },
    },
  };

  // === Pie Chart Config ===
  const pieConfig = useMemo(() => {
    return {
      labels: pieData.map((d) => d.name),
      datasets: [
        {
          data: pieData.map((d) => d.value),
          backgroundColor: ["#6366f1", "#10b981", "#f59e0b"],
          borderWidth: isDarkMode ? 0 : 2,
          borderColor: isDarkMode ? "#1e293b" : "#ffffff",
          hoverOffset: 10,
        },
      ],
    };
  }, [pieData, isDarkMode]);

  const pieOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: textColor,
          font: { size: 12 },
          padding: 16,
        },
      },
      title: {
        display: true,
        text: "Recipient Breakdown",
        color: titleColor,
        font: { size: 14, weight: "bold" as const },
      },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: "#f1f5f9",
        bodyColor: "#cbd5e1",
        cornerRadius: 8,
        padding: 10,
      },
    },
  };

  // === Campaign summary numbers ===
  const rates = graphData?.get_campaign_stats_rates || {};
  const hasTimelineData = uniqueOpens.length > 0;
  const hasSummaryData = summarySeries.some((item) => item.value > 0);
  const hasGraphData = hasTimelineData || hasSummaryData || pieData.some((item) => item.value > 0);

  if (isLoading) {
    return (
      <div className="space-y-6 px-6 py-4 fade-in">
        <Card className="p-4 shadow-sm border border-line dark:border-dark-600 bg-surface dark:bg-dark-800 rounded-xl">
          <Skeleton className="h-[280px] w-full rounded-lg" />
        </Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Card className="p-4 border border-line dark:border-dark-600 bg-surface dark:bg-dark-800 shadow-sm rounded-xl">
            <Skeleton className="h-[260px] w-full rounded-lg" />
          </Card>
          <Card className="p-4 border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 rounded-xl">
            <div className="space-y-3">
              <Skeleton className="h-10 w-full rounded-lg" />
              <Skeleton className="h-10 w-full rounded-lg" />
              <Skeleton className="h-10 w-full rounded-lg" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (isABCampaign(graphData)) {
    return <div className="px-6 py-4"><ABCampaignResults key={campaignStatsDetails.id} campaignId={campaignStatsDetails.id} initialStats={graphData} /></div>;
  }

  if (!hasGraphData) {
    return (
      <div className="px-6 py-8">
        <Card className="p-6 border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 rounded-xl text-center">
          <h6 className="text-[14px] font-bold text-content dark:text-white">
            No graph data available
          </h6>
          <p className="mt-2 text-[13px] text-content-muted dark:text-gray-400">
            This campaign does not have engagement graph data yet.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-6 py-4 fade-in">
      {/* Line Chart */}
      <Card className="p-4 shadow-sm border border-line dark:border-dark-600 bg-surface dark:bg-dark-800 rounded-xl">
        <div className="h-[280px]">
          {hasTimelineData ? (
            <Line data={lineConfig} options={lineOptions} />
          ) : (
            <Bar data={summaryBarConfig} options={summaryBarOptions} />
          )}
        </div>
      </Card>

      {/* Pie Chart + Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
        {/* Pie Chart */}
        <Card className="p-4 border border-line dark:border-dark-600 bg-surface dark:bg-dark-800 shadow-sm rounded-xl">
          <div className="h-[260px]">
            <Pie className="text-content-subtle dark:text-gray-200" data={pieConfig} options={pieOptions} />
          </div>
        </Card>

        {/* Stats */}
        <Card className="p-4 border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 rounded-xl">
          <h6 className="text-[14px] font-bold text-content dark:text-white mb-4">
            Campaign Engagement Summary
          </h6>

          <div className="space-y-3">
            <Stat
              label="Opened"
              value={`${rates.opened_rate?.toFixed?.(0) || 0}%`}
              desc="of all recipients opened so far"
            />
            <Stat
              label="Clicked"
              value={`${rates.clicked_rate?.toFixed?.(0) || 0}%`}
              desc="clicked a link"
            />
            <Stat
              label="Unsubscribed"
              value={`${rates.unsubscribed_rate?.toFixed?.(0) || 0}%`}
              desc="unsubscribed"
            />
            <Stat
              label="Spam"
              value={`${rates.spam_rate?.toFixed?.(0) || 0}%`}
              desc="marked as spam"
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

// ===== Small Subcomponent =====
function Stat({
  label: _label,
  value,
  desc,
}: {
  label: string;
  value: string;
  desc: string;
}) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-line-subtle dark:border-dark-700 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-medium text-content-muted dark:text-gray-400">{desc}</p>
      </div>
      <span className="text-[15px] font-bold text-blue-600 dark:text-blue-400 flex-shrink-0">{value}</span>
    </div>
  );
}
